import { Controller } from "@hotwired/stimulus"
import { jsonRequest, withRetry } from "@/lib/s3_multipart_upload"

const REGISTER_ATTEMPTS = 6

// Client document portal: stage files locally, upload everything when the
// prospect clicks Submit documents.
export default class extends Controller {
  static targets = [
    "section",
    "input",
    "dropzone",
    "stagedList",
    "stagedBadge",
    "stagedCount",
    "submitBtn",
    "errorAlert",
    "errorMessage"
  ]
  static outlets = ["multipart-dropzone"]

  static values = {
    uploadUrl: String,
    completeUrl: String,
    portalUrl: String
  }

  connect() {
    this.stagedByKind = new Map()
  }

  fileSelected(event) {
    this.stageFiles(event.target)
  }

  dragover(event) {
    event.preventDefault()
    event.currentTarget.classList.add("dragover")
  }

  dragleave(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("dragover")
  }

  drop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("dragover")

    const section = event.currentTarget.closest("[data-document-kind]")
    const input = section?.querySelector("[data-sales-document-portal-target='input']")
    const files = event.dataTransfer?.files
    if (!input || !files?.length) return

    const transfer = new DataTransfer()
    Array.from(files).forEach((file) => transfer.items.add(file))
    input.files = transfer.files
    this.stageFiles(input)
  }

  removeStaged(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const kind = button.dataset.kind
    const index = Number.parseInt(button.dataset.index, 10)
    const section = button.closest("[data-document-kind]")
    if (!section || !kind || Number.isNaN(index)) return

    const files = this.stagedByKind.get(kind) || []
    files.splice(index, 1)

    if (files.length) {
      this.stagedByKind.set(kind, files)
    } else {
      this.stagedByKind.delete(kind)
    }

    this.refreshSectionStaging(section, kind)
    this.clearError()
  }

  stageFiles(input) {
    const section = input.closest("[data-document-kind]")
    if (!section) return

    const kind = section.dataset.documentKind
    const files = Array.from(input.files || [])
    if (!files.length) return

    const multiFile = section.dataset.multiFile === "true"
    const existing = multiFile ? (this.stagedByKind.get(kind) || []) : []
    this.stagedByKind.set(kind, existing.concat(files))
    input.value = ""

    this.refreshSectionStaging(section, kind)
    this.clearError()
  }

  refreshSectionStaging(section, kind) {
    this.updateStagedBadge(section, kind)
    this.renderStagedList(section, kind)
    this.updateDropzone(section, kind)
  }

  renderStagedList(section, kind) {
    const list = section.querySelector("[data-sales-document-portal-target='stagedList']")
    if (!list) return

    const files = this.stagedByKind.get(kind) || []
    list.replaceChildren()

    files.forEach((file, index) => {
      const item = document.createElement("li")
      item.className = "sales-document-portal__staged-item"

      const icon = document.createElement("span")
      icon.className = "sales-document-portal__staged-icon"
      icon.innerHTML = `<i class="${this.fileIconClass(file)}" aria-hidden="true"></i>`

      const name = document.createElement("span")
      name.className = "sales-document-portal__staged-name"
      name.textContent = file.name
      name.title = file.name

      const meta = document.createElement("span")
      meta.className = "sales-document-portal__staged-size"
      meta.textContent = this.formatFileSize(file.size)

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "sales-document-portal__staged-remove btn btn-sm btn-link"
      remove.dataset.kind = kind
      remove.dataset.index = String(index)
      remove.dataset.action = "click->sales-document-portal#removeStaged"
      remove.setAttribute("aria-label", `Remove ${file.name}`)
      remove.innerHTML = '<i class="fa-regular fa-xmark" aria-hidden="true"></i>'

      item.append(icon, name, meta, remove)
      list.append(item)
    })

    list.classList.toggle("d-none", files.length === 0)
  }

  updateDropzone(section, kind) {
    const dropzone = section.querySelector("[data-sales-document-portal-target='dropzone']")
    if (!dropzone) return

    const hasStaged = (this.stagedByKind.get(kind) || []).length > 0
    dropzone.classList.toggle("sales-document-portal__dropzone--compact", hasStaged)
  }

  updateStagedBadge(section, kind) {
    const count = (this.stagedByKind.get(kind) || []).length
    const badge = section.querySelector("[data-sales-document-portal-target='stagedBadge']")
    const countEl = section.querySelector("[data-sales-document-portal-target='stagedCount']")
    if (!badge || !countEl) return

    countEl.textContent = count
    badge.classList.toggle("d-none", count === 0)
  }

  fileIconClass(file) {
    const extension = file.name.split(".").pop()?.toLowerCase() || ""
    const iconMap = {
      pdf: "fa-solid fa-file-pdf text-danger",
      doc: "fa-solid fa-file-word text-primary",
      docx: "fa-solid fa-file-word text-primary",
      xls: "fa-solid fa-file-excel text-success",
      xlsx: "fa-solid fa-file-excel text-success",
      csv: "fa-solid fa-file-csv text-info",
      txt: "fa-solid fa-file-lines text-secondary",
      jpg: "fa-solid fa-file-image text-info",
      jpeg: "fa-solid fa-file-image text-info",
      png: "fa-solid fa-file-image text-info",
      gif: "fa-solid fa-file-image text-info",
      webp: "fa-solid fa-file-image text-info"
    }

    return iconMap[extension] || "fa-solid fa-file text-muted"
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  stagedFileCount() {
    return Array.from(this.stagedByKind.values()).reduce((sum, files) => sum + files.length, 0)
  }

  async submitAll(event) {
    event.preventDefault()
    this.clearError()
    this.submitBtnTarget.disabled = true

    try {
      let uploadedSomething = false

      for (const outlet of this.multipartDropzoneOutlets) {
        const results = await outlet.upload()
        for (const { uploadId, file, registered } of results) {
          uploadedSomething = true
          if (registered) continue

          if (uploadId) {
            await this.registerMultipartUpload(outlet, uploadId)
          } else {
            await this.uploadFile(outlet.documentKindValue, file)
          }
          outlet.markRegistered()
        }
      }

      for (const [kind, files] of this.stagedByKind.entries()) {
        for (const file of files) {
          uploadedSomething = true
          await this.uploadFile(kind, file)
        }
      }

      if (!uploadedSomething) {
        throw new Error("Please choose a file before uploading.")
      }

      await this.completeRequest()
      window.location.assign(this.portalUrlValue)
    } catch (error) {
      this.showError(error.message || "Something went wrong. Please try again.")
      this.submitBtnTarget.disabled = false
    }
  }

  // Registration is idempotent on the server, so it is retried like the upload;
  // a gone session (404) means the next Submit has to upload again.
  async registerMultipartUpload(outlet, uploadId) {
    const payload = { document_kind: outlet.documentKindValue, upload_id: uploadId }

    try {
      await withRetry(() => jsonRequest(this.uploadUrlValue, "POST", payload), null, REGISTER_ATTEMPTS)
    } catch (error) {
      if (error.status === 404) outlet.discardUpload()
      throw error
    }
  }

  async uploadFile(kind, file) {
    const formData = new FormData()
    formData.append("document_kind", kind)
    formData.append("file[]", file)

    const response = await fetch(this.uploadUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken
      }
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) {
      throw new Error(data.error || "A file could not be uploaded.")
    }
  }

  async completeRequest() {
    const response = await fetch(this.completeUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken
      }
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) {
      throw new Error(data.error || "Could not submit your documents.")
    }
  }

  showError(message) {
    if (this.hasErrorAlertTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorAlertTarget.classList.remove("d-none")
      this.errorAlertTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }

  clearError() {
    if (this.hasErrorAlertTarget) {
      this.errorAlertTarget.classList.add("d-none")
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
